import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  TextField,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import config from '../../config';
import PrintIcon from '@mui/icons-material/Print';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import stationeryImage from '../../assets/Design+Print/package2020-1200x1113.jpg';
import heroImage from '../../assets/Design+Print/hero-image.jpg';

const PrintDesign = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    schoolOrBusinessName: '',
    phoneNumber: '',
    emailAddress: '',
    productDescription: '',
    message: '',
    howDidYouHear: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      // Create a new axios instance without the default Authorization header
      const publicAxios = axios.create({
        baseURL: config.apiUrl,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const response = await publicAxios.post('/api/stationery-enquiries', formData);

      if (response.data.success) {
        setSubmitSuccess(true);
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          schoolOrBusinessName: '',
          phoneNumber: '',
          emailAddress: '',
          productDescription: '',
          message: '',
          howDidYouHear: ''
        });
        
        // Scroll to success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      setSubmitError(error.response?.data?.message || 'Failed to submit enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    {
      icon: <PrintIcon sx={{ fontSize: 50, color: '#7B2CBF' }} />,
      title: 'Professional Printing',
      description: 'High-quality printing services for all your school and business needs. From newsletters to brochures, we deliver excellence.'
    },
    {
      icon: <DesignServicesIcon sx={{ fontSize: 50, color: '#7B2CBF' }} />,
      title: 'Custom Design',
      description: 'Our in-house graphic designers create custom designs that reflect your brand and message perfectly.'
    },
    {
      icon: <BusinessCenterIcon sx={{ fontSize: 50, color: '#7B2CBF' }} />,
      title: 'Stationery Solutions',
      description: 'Complete stationery packages including letterheads, business cards, envelopes, and more.'
    },
    {
      icon: <LocalOfferIcon sx={{ fontSize: 50, color: '#7B2CBF' }} />,
      title: 'Consistent Branding',
      description: 'Create a consistent brand across all your products for a streamlined professional look.'
    }
  ];

  const products = [
    'Business Cards',
    'Letterheads',
    'Envelopes',
    'Brochures',
    'Flyers',
    'Posters',
    'Banners',
    'Newsletters',
    'Certificates',
    'Invitations',
    'Programs',
    'And much more!'
  ];

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Hero Image */}
      <Box
        component="img"
        src={heroImage}
        alt="Design + Print"
        sx={{
          width: '100%',
          height: 'auto',
          display: 'block'
        }}
      />

      {/* Introduction Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Custom Design & Print Solutions
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 2 }}>
                Get all your stationery conveniently custom designed by our in house graphic designer.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 2 }}>
                Create a consistent brand across your products for a streamlined professional look!
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                Whether you're a school looking for professional newsletters and certificates, or a business needing branded stationery, we have the expertise and equipment to bring your vision to life.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={stationeryImage}
                alt="Stationery Package"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: 3
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 8, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 6, fontWeight: 600 }}>
            Our Services
          </Typography>
          
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 2 }}>
                      {service.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Products Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4, fontWeight: 600 }}>
            What We Can Print
          </Typography>
          
          <Paper elevation={3} sx={{ p: 4 }}>
            <Grid container spacing={2}>
              {products.map((product, index) => (
                <Grid item xs={6} sm={4} key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1,
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(123, 44, 191, 0.1)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#7B2CBF',
                        mr: 2
                      }}
                    />
                    <Typography variant="body1">{product}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Box sx={{ py: 8, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 6, fontWeight: 600 }}>
            Why Choose Austnews?
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 4, height: '100%', textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#7B2CBF', fontWeight: 700, mb: 2 }}>
                  40+ Years
                </Typography>
                <Typography variant="body1">
                  Of experience serving Australian schools and businesses
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 4, height: '100%', textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#7B2CBF', fontWeight: 700, mb: 2 }}>
                  In-House Team
                </Typography>
                <Typography variant="body1">
                  Professional graphic designers and printing specialists
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 4, height: '100%', textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#7B2CBF', fontWeight: 700, mb: 2 }}>
                  Quality Guaranteed
                </Typography>
                <Typography variant="body1">
                  High-quality materials and professional finishing
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact Form */}
      <Box id="contact-form" sx={{ py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 2, fontWeight: 600 }}>
            Contact our design team for a quote
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
            Fill out the form below and we'll get back to you with a custom quote for your print needs
          </Typography>
          
          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Thank you for your enquiry! Our design team will contact you soon with a quote.
            </Alert>
          )}
          
          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}
          
          <Paper elevation={3} sx={{ p: 4 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="School/Business Name"
                    name="schoolOrBusinessName"
                    value={formData.schoolOrBusinessName}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Phone Number"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Product Description"
                    name="productDescription"
                    placeholder="e.g., Business cards, letterheads, brochures..."
                    value={formData.productDescription}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={4}
                    label="Your Message"
                    name="message"
                    placeholder="Please provide details about your print requirements, quantities, and any specific design needs..."
                    value={formData.message}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    select
                    label="How did you hear about us?"
                    name="howDidYouHear"
                    value={formData.howDidYouHear}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="google">Google</MenuItem>
                    <MenuItem value="facebook">Facebook</MenuItem>
                    <MenuItem value="currentSchool">Current Austnews School</MenuItem>
                    <MenuItem value="referral">Referral</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={submitting}
                    sx={{
                      backgroundColor: '#7B2CBF',
                      '&:hover': {
                        backgroundColor: '#6A1FA8'
                      },
                      py: 1.5
                    }}
                  >
                    {submitting ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, backgroundColor: '#6B7280', color: 'white', textAlign: 'center' }}>
        <Container>
          <Typography variant="body2">
            Copyright Australian Newsletter Services Pty Ltd 2024 |{' '}
            <Box
              component="a"
              href="/design-print/terms-and-conditions"
              sx={{
                color: 'white',
                textDecoration: 'underline',
                '&:hover': {
                  color: '#E0E0E0'
                }
              }}
            >
              Terms & Conditions
            </Box>
            {' '}| All Rights Reserved
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            QAssure Number: 11954 | Accredited GITC Supplier: Q-6019 | ABN 24 010 367 478
          </Typography>
          <Typography sx={{ mt: 1, fontSize: '0.6rem', opacity: 0.4, letterSpacing: '0.05em' }}>
            MIS
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default PrintDesign;

